<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';

// components
import Carousel from 'primevue/carousel';
import Button from 'primevue/button';
import Tag from 'primevue/tag';

import useLayout from '@/composables/useLayout';
import useCreateNewDialog from '@/composables/useCreateNewDialog';
import usePermissionsStore from '@/stores/permissions';

import quickActions from '@/data/quickActions';

const permissionsStore = usePermissionsStore();
const createNewDialog = useCreateNewDialog();
const { hasSportradar } = storeToRefs(permissionsStore);
const { layoutState, toggleActionCards } = useLayout();

const actionCardsCollapsed = computed(() => layoutState.value.actionCardsCollapsed);

const collapseIcon = computed(() => (
  actionCardsCollapsed.value
    ? 'fa-regular fa-up-right-and-down-left-from-center'
    : 'fa-regular fa-down-left-and-up-right-to-center'
));

const actionCardsCollapsedTipText = computed(() => {
  const action = actionCardsCollapsed.value ? 'Show' : 'Hide';
  return `${action} full description.`;
});

const openQuickAction = (button) => {
  if (button.createType) {
    createNewDialog.open(button.createType);
    return;
  }

  button.action?.();
};

const navButtonStyle = {
  position: 'absolute',
  top: '50%',
  zIndex: 1,
  backgroundColor: 'white',
  borderRadius: '50%',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
};

const carouselPt = {
  root: { style: { overflow: 'visible' } },
  content: { class: 'carousel-content', style: { position: 'relative', overflow: 'visible' } },
  previousButton: {
    class: 'carousel-nav-btn',
    style: { ...navButtonStyle, left: '0', transform: 'translateX(-50%) translateY(-50%)' },
  },
  nextButton: {
    class: 'carousel-nav-btn',
    style: { ...navButtonStyle, right: '0', transform: 'translateX(50%) translateY(-50%)' },
  },
};

const responsiveOptions = ref([
  {
    breakpoint: '2800px',
    numVisible: 5,
    numScroll: 1,
  },
  {
    breakpoint: '2500px',
    numVisible: 4,
    numScroll: 1,
  },
  {
    breakpoint: '1500px',
    numVisible: 3,
    numScroll: 1,
  },
  {
    breakpoint: '1350px',
    numVisible: 3,
    numScroll: 1,
  },
  {
    breakpoint: '1024px',
    numVisible: 2,
    numScroll: 1,
  },
  {
    breakpoint: '768px',
    numVisible: 1,
    numScroll: 1,
  },
]);

</script>

<template>
  <section class="px-6" data-test="news-cards">
    <div class="flex flex-row-reverse">
      <Button
        v-tooltip.left="actionCardsCollapsedTipText"
        @click="toggleActionCards"
        outlined
        class="text-color"
        :data-test="actionCardsCollapsed
          ? 'quick-action-cards-expand'
          : 'quick-action-cards-collapse'"
      >
        <template #icon>
          <FontAwesomeIcon :icon="collapseIcon" />
        </template>
      </Button>
    </div>
    <Carousel
      :value="quickActions"
      :numVisible="8"
      :numScroll="3"
      :responsiveOptions="responsiveOptions"
      :showIndicators="false"
      :pt="carouselPt"
      data-test="quick-action-carousel"
    >
      <template #item="slotProps">
        <div
          class="
            quick-action-card
            border-1
            surface-border
            border-round
            xl:mx-3
            sm:mx-2
            flex
            flex-column
            justify-content-between
            relative
            overflow-hidden
          "
          :class="{ 'quick-action-card--collapsed': actionCardsCollapsed }"
          :data-test="slotProps.data.dataTest"
        >
          <div
            class="quick-action-card__flag absolute"
          >
            <Tag
              v-if="slotProps.data.flag === 'new'"
              :value="`New`"
              rounded
              class=" p-tag-callout"
            />
            <font-awesome-icon
              v-if="slotProps.data.flag === 'pro'"
              class="quick-action-card__flag--bolt"
              icon="fa-solid fa-bolt-lightning"
            />
          </div>
          <transition name="expand">
            <div v-if="!actionCardsCollapsed" class="quick-action-card__media">
              <span
                class="inline-svg-container quick-action-card__img quick-action-card__img--svg"
                v-if="slotProps.data.image.type === 'svg'"
                v-html="slotProps.data.image.src"
                role="img"
                aria-label="slotProps.data.image.alt"
              />
              <img
                v-else
                :src="slotProps.data.image.src"
                :alt="slotProps.data.image.alt"
                class="quick-action-card__img"
              />
            </div>
          </transition>
          <div class="quick-action-card__content">
            <h3 class="m-0 font-bold">{{ slotProps.data.name }}</h3>
            <transition name="expand">
              <p
                v-if="!actionCardsCollapsed"
                class="quick-action-card__description"
                v-html="slotProps.data.description"
              />
            </transition>
            <div class="quick-action-card__cta">
              <component
                :is="slotProps.data.button.url
                  || slotProps.data.button.action
                  || slotProps.data.button.createType
                  ? 'a'
                  : 'router-link'"
                :to="slotProps.data.button.route"
                :href="slotProps.data.button.sportradar && !hasSportradar
                  ? slotProps.data.button.url2
                  : slotProps.data.button.url"
                :target="slotProps.data.button.sportradar
                  ? '_blank'
                  : (slotProps.data.button.url || slotProps.data.button.action
                    ? '_blank'
                    : '_self')"
                @click="openQuickAction(slotProps.data.button)"
                class="quick-action-card__link no-underline"
                :data-entity="slotProps.data.dataEntity"
              >
                <Button
                  :label="slotProps.data.button.content"
                  :severity="slotProps.data.button.severity"
                  :outlined="slotProps.data.button.outlined"
                  :class="slotProps.data.button.classes"
                  :data-test="`${slotProps.data.dataTest}-button`"
                  raised
                  rounded
                />
              </component>
            </div>
          </div>
        </div>
      </template>
    </Carousel>
  </section>
</template>

<style lang="scss">
.quick-action-card {
  min-width: 200px;
  max-width: 500px;
  height: 100%;
  background-color: var(--surface-card);
  box-shadow:
    0px 1px 10px rgba(0, 0, 0, 0.12),
    0px 4px 5px rgba(0, 0, 0, 0.14),
    0px 2px 4px -1px rgba(0, 0, 0, 0.2);

  &__media {
    width: 100%;
    height: 125px;
    background-color: var(--surface-100);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__content {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;
    text-align: left;
    flex: 1;
    font-size: 0.8em;

    h3 {
      font-size: 1rem;
    }

    .p-button {
      font-size: 1em;
    }
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__img--svg {
    padding: 1.25rem;
  }

  &__description {
    margin: 0;
    line-height: 1.45;
    color: var(--text-color-secondary);
  }

  &__cta {
    margin-top: auto;
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }

  &__link {
    display: flex;
    justify-content: flex-end;
  }

  &--collapsed &__content h3 {
    padding-right: 2.5rem;
  }

  &__flag {
    top: 8px;
    right: 8px;

    &--bolt {
      color: var(--tertiary-color);
    }
  }

}
.carousel-nav-btn {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.carousel-content:hover .carousel-nav-btn:not(:disabled) {
  opacity: 1;
}

.p-carousel-items-content {
  padding-top: .5rem;
  padding-bottom: .5rem;
}

.expand-enter-from, .expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.expand-enter-to, .expand-leave-from {
  max-height: 600px;
  opacity: 1;
}

.expand-enter-active, .expand-leave-active {
  transition: max-height 0.7s ease-in-out, opacity 0.3s ease-in-out 0.3s;
  overflow: hidden;
}
</style>
